import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { ProductService } from '../../services/productservice';
import { Product } from '../../services/product';
import { SearchService } from '../../services/search-service.service';
// import { LoaderService } from '../../services/loader.service'; // Remove if only used for spinners in this template

const URL_PATH_EVENTS = '/events';
const URL_PATH_ACTIVITIES = '/activities';
const URL_PATH_DEALS_OFFERS = '/dealsandoffers';
const URL_PATH_ATTRACTIONS = '/attractions';
const URL_PATH_ACCOMMODATION = '/accommodation';

// Constants
const ACT_WHATTODO: string = 'Things to do';
const ACT_WHATTOSEE: string = 'What to see';
const ACT_WHERETOSTAY: string = 'Where to stay';
const CAT_EVENTS: string = 'EVENT';
const CAT_TOUR: string = 'TOUR';
const CAT_DEALS_AND_OFFERS: string = 'TAGDeals-Offers';
const CAT_ATTRACTIONS: string = 'ATTRACTION';
const CAT_ACCOMM: string = 'ACCOMM';
const CLASSIFICATION_WALKS_TRAILS: string = 'WBTOURS,TRAIL';
const DEFAULT_API_PAGE_SIZE: number = 12;

@Component({
  selector: 'app-categories',
  templateUrl: './categories.component.html',
  styleUrls: ['./categories.component.scss'],
})
export class CategoriesComponent implements OnInit, OnDestroy {
  // --- Properties ---
  products: Product[];
  filteredData: Product[] = [];
  public readonly DealsOffersCategoryIdentifier = CAT_DEALS_AND_OFFERS;
  productCategory: any;
  innerItems: Product[] = [];
  searchCategories: any = [];
  activityIcon: any;
  filterCategory: any;
  catName: any;
  language: string;
  selectedChildItem: any;
  responsiveOptions: any;
  responsiveOptionsActivity: any;
  subTabCarouselStates: Map<string, any> = new Map();
  activeChildCategoryKey: string = '';
  currentViewport: string = 'desktop';
  private windowResizeHandler: (() => void) | null = null;

  constructor(
    private productService: ProductService,
    private searchService: SearchService,
    private router: Router,
    private translate: TranslateService // private loaderService: LoaderService // Remove if only used for spinners
  ) {
    // Responsive options
    this.responsiveOptions = [
      { breakpoint: '1024px', numVisible: 4, numScroll: 1 },
      { breakpoint: '768px', numVisible: 3, numScroll: 1 },
      { breakpoint: '640px', numVisible: 2, numScroll: 1 },
      { breakpoint: '480px', numVisible: 1, numScroll: 1 },
    ];
    this.responsiveOptionsActivity = [
      { breakpoint: '1024px', numVisible: 3, numScroll: 1 },
      { breakpoint: '768px', numVisible: 2, numScroll: 1 },
      { breakpoint: '640px', numVisible: 2, numScroll: 1 },
      { breakpoint: '480px', numVisible: 2, numScroll: 1 },
    ];

    this.setCurrentViewport();
    this.windowResizeHandler = this.onWindowResize.bind(this);
    window.addEventListener('resize', this.windowResizeHandler);
  }

  ngOnInit(): void {
    this.language = localStorage.getItem('language') ?? 'en';
    this.loadChildData();

    this.productService.getActivitycategories().then((products) => {
      this.products = products;
      const defaultOuterCategory = this.products?.find((p) => p.id === 1);
      if (defaultOuterCategory) {
        this.getItems(defaultOuterCategory);
      } else if (this.products && this.products.length > 0) {
        this.getItems(this.products[0]);
      }
    });

    // Styling adjustments
    setTimeout(() => {
      if (this.language.toLowerCase() === 'en') {
        document.querySelector('.catsection')?.classList.add('catsection-lang');
      }
      if (this.language.toLowerCase() === 'ru') {
        document.querySelector('.viewall-btn')?.classList.add('homeviewall-btn-moreright');
      }
    }, 200);
  }

  ngOnDestroy(): void {
    if (this.windowResizeHandler) {
      window.removeEventListener('resize', this.windowResizeHandler);
    }
  }

  // --- ADDED trackBy function ---
  /**
   * Tracks items in the *ngFor loop for sub-tabs (p-tabPanel).
   * Helps Angular optimize rendering when the list changes.
   * @param index The index of the item.
   * @param childitem The sub-tab item itself.
   * @returns A unique identifier (e.g., id or name).
   */
  public trackByChildItemId(index: number, childitem: any): string | number {
    return childitem.id || childitem.name || index; // Use id, fallback to name, finally index
  }
  // --- END trackBy function ---

  public generateCategoryKey(childitem: any): string {
    if (!childitem) return '';
    const baseKey = childitem.category || childitem.name || '';
    return baseKey
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  onWindowResize(): void {
    this.setCurrentViewport();
  }

  setCurrentViewport(): void {
    const width = window.innerWidth;
    if (width >= 1024) this.currentViewport = 'desktop';
    else if (width >= 768) this.currentViewport = 'large-tablet';
    else if (width >= 640) this.currentViewport = 'tablet';
    else if (width >= 480) this.currentViewport = 'small-tablet';
    else this.currentViewport = 'mobile';
  }

  getItemsPerPage(): number {
    if (this.currentViewport === 'desktop') return 4;
    if (this.currentViewport === 'large-tablet') return 4;
    if (this.currentViewport === 'tablet') return 3;
    if (this.currentViewport === 'small-tablet') return 2;
    return 1;
  }

  getCarouselState(categoryKey: string): any {
    if (!this.subTabCarouselStates.has(categoryKey)) {
      this.subTabCarouselStates.set(categoryKey, {
        pageNumber: 1,
        hasMoreItems: true,
        isLoadingMore: false,
        currentPageIndex: 0,
        totalResults: 0,
        loadedItemsCount: 0,
        apiPageSize: DEFAULT_API_PAGE_SIZE,
        items: [],
        categoryApiValue: '',
        classificationsApiValue: null,
        tagApiValue: null,
      });
    }
    return this.subTabCarouselStates.get(categoryKey);
  }

  getItems(item: any): void {
    if (!item) return;
    this.productCategory = item.name;
    this.filteredData = this.performFilter(item.id);

    if (this.filteredData.length > 0 && this.filteredData[0]?.child && this.filteredData[0].child.length > 0) {
      this.getChildItems(this.filteredData[0].child[0]);
    } else {
      this.searchCategories = [];
      this.activeChildCategoryKey = '';
    }
  }

  /*getChildItems(child: Product): void {
    if (!child) return;

    this.selectedChildItem = child;
    this.catName = child.name;
    this.activityIcon = child.iconUrl;
    this.filterCategory = child.category;
    const categoryKey = this.generateCategoryKey(child);
    this.activeChildCategoryKey = categoryKey;

    let classifications: string | null = null;
    let categoryApiValue: string | null = child.category;
    let tagApiValue: string | null = null;
    const translatedWalksTrails = this.translate.instant('Walks and Trails');
    const translatedDealsOffers = this.translate.instant('Deals & Offers');

    if (this.catName === translatedWalksTrails) {
      categoryApiValue = '';
      classifications = CLASSIFICATION_WALKS_TRAILS;
    } else if (this.filterCategory === CAT_DEALS_AND_OFFERS || this.catName === translatedDealsOffers) {
      categoryApiValue = '';
      tagApiValue = CAT_DEALS_AND_OFFERS;
      this.filterCategory = CAT_DEALS_AND_OFFERS;
    }
    this.loadFreshSubTabData(categoryKey, categoryApiValue, classifications, tagApiValue);
  }*/

  loadFreshSubTabData(
    categoryKey: string,
    categoryApiValue: string | null,
    classifications: string | null,
    tagApiValue: string | null
  ): void {
    const state = this.getCarouselState(categoryKey);
    state.pageNumber = 1;
    state.hasMoreItems = true;
    state.isLoadingMore = true;
    state.items = [];
    state.totalResults = 0;
    state.loadedItemsCount = 0;
    state.currentPageIndex = 0;
    state.apiPageSize = DEFAULT_API_PAGE_SIZE;
    state.categoryApiValue = categoryApiValue;
    state.classificationsApiValue = classifications;
    state.tagApiValue = tagApiValue;

    this.searchCategories = [];
    // this.loaderService.show(); // Removed loader call

    this.searchService
      .planYourVisitSearchListing(
        state.categoryApiValue,
        null,
        state.classificationsApiValue,
        null,
        state.tagApiValue,
        state.pageNumber
      )
      .subscribe({
        next: (data) => {
          this.processSubTabSearchResults(data, categoryKey);
          // this.loaderService.hide(); // Removed loader call
        },
        error: (error) => {
          state.isLoadingMore = false;
          state.hasMoreItems = false;
          // this.loaderService.hide(); // Removed loader call
          console.error(`Error loading initial data for category ${categoryKey}:`, error);
        },
      });
  }

  onSubTabCarouselPageChange(event: any): void {
    if (!this.activeChildCategoryKey) return;
    const state = this.getCarouselState(this.activeChildCategoryKey);
    state.currentPageIndex = event.page;
    const itemsPerPage = this.getItemsPerPage();
    const totalCarouselPages = state.items.length > 0 ? Math.ceil(state.items.length / itemsPerPage) : 1;

    if (state.currentPageIndex >= totalCarouselPages - 1 && state.hasMoreItems && !state.isLoadingMore) {
      this.loadMoreSubTabItems(this.activeChildCategoryKey);
    }
  }

  loadMoreSubTabItems(categoryKey: string): void {
    const state = this.getCarouselState(categoryKey);
    if (!state.hasMoreItems || state.isLoadingMore) return;
    if (state.totalResults > 0 && state.loadedItemsCount >= state.totalResults) {
      state.hasMoreItems = false;
      return;
    }

    state.isLoadingMore = true;
    state.pageNumber++;
    // this.loaderService.show(); // Removed loader call

    this.searchService
      .planYourVisitSearchListing(
        state.categoryApiValue,
        null,
        state.classificationsApiValue,
        null,
        state.tagApiValue,
        state.pageNumber
      )
      .subscribe({
        next: (data) => {
          this.processSubTabSearchResults(data, categoryKey);
          // this.loaderService.hide(); // Removed loader call
        },
        error: (error) => {
          state.isLoadingMore = false;
          state.pageNumber--;
          // this.loaderService.hide(); // Removed loader call
          console.error(`Error loading more items for category ${categoryKey}, page ${state.pageNumber + 1}:`, error);
        },
      });
  }

  async processSubTabSearchResults(data: any, categoryKey: string): Promise<void> {
    const state = this.getCarouselState(categoryKey);
    if (!data) {
      state.isLoadingMore = false;
      state.hasMoreItems = false;
      console.warn(`Received null/undefined data for category ${categoryKey}, page ${state.pageNumber}`);
      if (categoryKey === this.activeChildCategoryKey) {
        this.searchCategories = [...state.items];
      }
      return;
    }

    if (data.numberOfResults !== undefined && data.numberOfResults !== null) {
      state.totalResults = data.numberOfResults;
    }
    const products = data.products || [];

    if (products.length > 0) {
      if (state.pageNumber === 1) {
        state.apiPageSize = products.length;
        state.loadedItemsCount = 0;
      }

      const processedProductsPromises = products.map(async (mData: any) => {
        if (!mData || !mData.productId) return null;
        try {
          const detailData = await this.searchService.getViewListingDetailsEn(mData.productId).toPromise();
          if (!detailData) return null;
          let hasDeals = false;
          if (state.tagApiValue !== CAT_DEALS_AND_OFFERS && detailData.attributes) {
            hasDeals = detailData.attributes.some((att: any) => att.attributeId === 'Deals&Offers');
          }
          if (hasDeals) return null;
          mData.productName = detailData.productName || 'Name not available';
          if (mData.productImage && typeof mData.productImage === 'string') {
            try {
              let parts = mData.productImage.split('&');
              if (parts.length >= 3) {
                parts[1] = 'fit=crop&w=460';
                parts[2] = 'h=600';
                mData.productImage = parts.join('&');
              } else if (parts.length === 1 && !mData.productImage.includes('?')) {
                mData.productImage += '?fit=crop&w=460&h=600';
              }
            } catch (e) {
              /* ignore img error */
            }
          } else {
            mData.productImage = null;
          }
          return mData;
        } catch (error) {
          console.error(`Error processing product ${mData.productId}:`, error);
          return null;
        }
      });

      const newProducts = (await Promise.all(processedProductsPromises)).filter((item) => item !== null);
      state.items = [...state.items, ...newProducts];
      state.loadedItemsCount += newProducts.length;
      if (state.totalResults > 0) {
        state.hasMoreItems = state.loadedItemsCount < state.totalResults;
      } else {
        state.hasMoreItems = newProducts.length >= state.apiPageSize;
      }
    } else {
      state.hasMoreItems = false;
    }

    if (categoryKey === this.activeChildCategoryKey) {
      this.searchCategories = [...state.items];
    }
    state.isLoadingMore = false;
  }

  performFilter(filterById: number): any[] {
    const found = this.innerItems?.find((product: any) => product.id === filterById);
    return found ? [found] : [];
  }

  loadChildData(): void {
    this.innerItems = [];
    this.productService.getWhatToDo().then((wproducts) => {
      const filteredW = wproducts?.filter((item: any) => item.url !== '/walkandtrails') || [];
      this.innerItems.push({ id: 1, name: ACT_WHATTODO, description: '', child: this.formatValues(filteredW, 1) });
      this.productService.getWhatToSee().then((sProducts) => {
        this.innerItems.push({
          id: 2,
          name: ACT_WHATTOSEE,
          description: '',
          child: this.formatValues(sProducts || [], 2),
        });
        this.productService.getWhereToStay().then((hProducts) => {
          this.innerItems.push({
            id: 3,
            name: ACT_WHERETOSTAY,
            description: '',
            child: this.formatValues(hProducts || [], 3),
          });
        });
      });
    });
  }

  formatValues(items: Product[], parentIndex: number): Product[] {
    if (!items) return [];

    return items.map((item: any) => {
      let determinedCategory: string | null = null;
      const itemUrl = item.url ? item.url.toLowerCase() : '';

      if (itemUrl.endsWith(URL_PATH_DEALS_OFFERS)) {
        determinedCategory = CAT_DEALS_AND_OFFERS;
      } else if (itemUrl.endsWith(URL_PATH_EVENTS)) {
        determinedCategory = CAT_EVENTS;
        item.iconUrl = 'icon-events';
      } else if (itemUrl.endsWith(URL_PATH_ACTIVITIES)) {
        determinedCategory = CAT_TOUR;
        item.iconUrl = 'icon-activity';
      } else if (itemUrl.endsWith(URL_PATH_ATTRACTIONS)) {
        determinedCategory = CAT_ATTRACTIONS;
      } else if (itemUrl.endsWith(URL_PATH_ACCOMMODATION)) {
        determinedCategory = CAT_ACCOMM;
      }

      return { ...item, category: determinedCategory };
    });
  }

  getChildItems(child: Product): void {
    if (!child) return;

    this.selectedChildItem = child;
    this.catName = child.name;
    this.activityIcon = child.iconUrl;
    this.filterCategory = child.category; // This is set by formatValues using URL
    const categoryKey = this.generateCategoryKey(child);
    this.activeChildCategoryKey = categoryKey;

    let classifications: string | null = null;
    let categoryApiValue: any = child.category;
    let tagApiValue: string | null = null;
    const translatedWalksTrails = this.translate.instant('Walks and Trails');

    // Special handling for "Walks and Trails" (usually by name and results in classifications)
    if (this.catName === translatedWalksTrails) {
      categoryApiValue = ''; // API expects empty category for this specific case
      classifications = CLASSIFICATION_WALKS_TRAILS;
    }
    // Special handling for "Deals & Offers" (results in a tag)
    else if (this.filterCategory === CAT_DEALS_AND_OFFERS) {
      categoryApiValue = ''; // API expects empty category for deals
      tagApiValue = CAT_DEALS_AND_OFFERS; // API uses tag for deals
    }
    this.loadFreshSubTabData(categoryKey, categoryApiValue, classifications, tagApiValue);
  }

  getCategoryItems(product: any): void {
    if (product?.productId) window.open(`/listingdetails/${product.productId}`, '_blank');
  }

  getUrlForCategory(categoryName: string): string {
    if (!categoryName) return '/experience/events';
    const transName = this.translate.instant(categoryName);
    const routes: { [key: string]: string } = {
      [this.translate.instant('Activities')]: '/experience/activities',
      [this.translate.instant('Attractions')]: '/experience/attractions',
      [this.translate.instant('Accommodation')]: '/planyourvisit/wheretostay',
      [this.translate.instant('Deals & Offers')]: '/experience/events?tag=' + CAT_DEALS_AND_OFFERS,
      [this.translate.instant('Events')]: '/experience/events',
      [this.translate.instant('Walks and Trails')]: '/experience/walksandtrails',
    };
    return routes[transName] || '/experience/events';
  }

  thingTODOPageSelection(catName: string): void {
    const event = arguments[arguments.length - 1] instanceof Event ? arguments[arguments.length - 1] : null;
    if (!catName) return;
    if (this.translate.instant(catName) === this.translate.instant('Deals & Offers')) {
      event?.preventDefault();
      this.router.navigate(['/experience/events'], { queryParams: { tag: CAT_DEALS_AND_OFFERS } });
    }
  }

  isSelected(childitem: any): boolean {
    return this.selectedChildItem === childitem;
  }
}
